import { FieldValueLookupError } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/errors/field-value-lookup-error.tsx';
import type {
	CascadingSelectPublicSetValue,
	CascadingSelectOptionValue,
	CascadingSelectPublicGetValue,
} from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/fields/cascading-select';
import { isNotEmptyString } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/is-not-empty-string';
import { singleValueLookup } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/single-value-lookup';

export type CascadingSelectInternalSetValue = null | {
	value: string;
	label: string;
	child: null | {
		value: string;
		label: string;
	};
};

export type CascadingSelectInternalValue = {
	parentOptionValue: CascadingSelectOptionValue;
	childOptionValues: CascadingSelectOptionValue[] | [];
};

const findCascadingSelectLookupValues = (
	lookupData: CascadingSelectInternalValue[],
	parentId: string,
	childId: string | null,
) => {
	const foundOption = lookupData.find(
		(currentOption) => currentOption.parentOptionValue.id === parentId,
	);
	if (!foundOption) {
		throw new FieldValueLookupError();
	}

	const value = {
		parent: foundOption.parentOptionValue,
		child:
			childId !== null
				? singleValueLookup<CascadingSelectOptionValue>(
						childId,
						foundOption.childOptionValues,
						'id',
					)
				: childId,
	};

	return value;
};

export const transformSetValueCascadingSelect = (
	value: CascadingSelectPublicSetValue,
	allowedValues: CascadingSelectInternalValue[],
): CascadingSelectInternalSetValue => {
	if (value === null) {
		return null;
	}

	const cascadingSelectValue = findCascadingSelectLookupValues(
		allowedValues,
		value.parentId,
		value.childId,
	);

	if (!isNotEmptyString(cascadingSelectValue.parent.value)) {
		throw new FieldValueLookupError();
	}

	return {
		label: cascadingSelectValue.parent.value,
		value: cascadingSelectValue.parent.id,
		child: cascadingSelectValue.child
			? {
					label: cascadingSelectValue.child.value,
					value: cascadingSelectValue.child.id,
				}
			: null,
	};
};

export const transformGetValueCascadingSelect = (
	cascadingValue: CascadingSelectInternalSetValue,
): CascadingSelectPublicGetValue => {
	if (!cascadingValue) {
		return null;
	}

	const { label, value, child } = cascadingValue;

	return child?.label && child?.value
		? {
				parent: { id: value, value: label },
				child: { id: child.value, value: child.label },
			}
		: {
				parent: { id: value, value: label },
				child: null,
			};
};
